import type { Client, PickerFileMetadata, PickerResponse } from 'filestack-js';
import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type {
  FacebookCampaign,
  FacebookDisplayCreative,
  IDimensionDefinition,
  IFacebook,
} from '@feathr/blackbox';
import {
  CreativeClass,
  FacebookVideoCreative,
  META_IMAGE_SIZE_LIMIT,
  META_VIDEO_CONTENT_TYPES,
  META_VIDEO_SIZE_LIMIT,
  metaAspectRatiosMatch,
} from '@feathr/blackbox';
import { Button, MenuItem, toast } from '@feathr/components';
import { useAccount } from '@feathr/extender/state';
import { useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

interface IProps {
  creatives: IObservableArray<FacebookDisplayCreative>;
  campaign: FacebookCampaign;
  isMenuItem?: boolean;
}

interface IVideoDimensionDefintion {
  width: number;
  height: number;
  duration: number;
}

interface IValidationResult {
  isValid: boolean;
  isAspectRatioValid: boolean;
  meetsMinDimensions: boolean;
  minWidth: number;
  minHeight: number;
  matchingSpec: string;
}

function getVideoDimensions(url: string): Promise<IVideoDimensionDefintion> {
  return new Promise<IVideoDimensionDefintion>((resolve) => {
    const video = document.createElement('video');
    video.addEventListener('loadedmetadata', () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
      });
    });
    video.src = url;
  });
}

async function uploadToFacebook(
  creative: FacebookVideoCreative,
  fbAccountId?: string,
  accessToken?: string,
): Promise<void> {
  if (!fbAccountId || !accessToken) {
    toast('Failed to upload video to Meta.', {
      type: 'error',
    });
    return;
  }
  const body = new FormData();
  body.append('access_token', accessToken);
  body.append('file_url', creative.get('url'));
  const response = await fetch(
    `https://graph.facebook.com/${FACEBOOK_API_VERSION}/${fbAccountId}/advideos`,
    {
      body,
      method: 'POST',
    },
  );
  const json = await response.json();
  const videoId = json.id;
  if (videoId) {
    creative.set({ ad_video_id: videoId });
    toast('Video uploaded to Meta', { type: 'success' });
  }
}

/**
 * Finds the best matching spec for the given dimensions and aspect ratio.
 * Prefers exact match, then falls back to smallest valid spec.
 */
function findMatchingSpec(
  matchingDimensions: IDimensionDefinition[],
  width: number,
  height: number,
): string {
  const exactMatch = matchingDimensions.find((dim) => dim.width === width && dim.height === height);

  if (exactMatch) {
    return exactMatch.spec;
  }

  const sortedBySize = [...matchingDimensions].sort(
    (a, b) => a.width * a.height - b.width * b.height,
  );

  const bestMatch = sortedBySize.find((dim) => dim.width >= width && dim.height >= height);
  return (bestMatch || sortedBySize[0]).spec;
}

/**
 * Validates creative dimensions against Meta's requirements.
 * Checks aspect ratio (with tolerance) and minimum dimensions.
 */
function validateMetaCreativeDimensions(
  width: number,
  height: number,
  creativeAspectRatio: number,
  validDimensions: Record<string, IDimensionDefinition>,
): IValidationResult {
  const validAspectRatioValues = Object.values(validDimensions).map(
    (dim) => dim.width / dim.height,
  );

  const isAspectRatioValid = validAspectRatioValues.some((validRatio) =>
    metaAspectRatiosMatch(creativeAspectRatio, validRatio),
  );

  if (!isAspectRatioValid) {
    return {
      isValid: false,
      isAspectRatioValid: false,
      meetsMinDimensions: false,
      minWidth: 0,
      minHeight: 0,
      matchingSpec: '',
    };
  }

  const matchingDimensions = Object.values(validDimensions).filter((dim) =>
    metaAspectRatiosMatch(dim.width / dim.height, creativeAspectRatio),
  );

  const minWidth = Math.min(...matchingDimensions.map((dim) => dim.width));
  const minHeight = Math.min(...matchingDimensions.map((dim) => dim.height));
  const meetsMinDimensions = width >= minWidth && height >= minHeight;
  const matchingSpec = findMatchingSpec(matchingDimensions, width, height);

  return {
    isValid: meetsMinDimensions,
    isAspectRatioValid: true,
    meetsMinDimensions,
    minWidth,
    minHeight,
    matchingSpec,
  };
}

function sanitizeFilename(filename: string): string {
  return filename.replaceAll(/[^a-zA-Z0-9_\-\s.]/g, '').replaceAll(/\s+/g, '_');
}

function AddCreativeButton({ creatives, campaign, isMenuItem }: IProps): JSX.Element {
  const { Creatives } = useStore();
  const account = useAccount();
  const integration = account.get('facebook', {} as IFacebook);
  const { t } = useTranslation();

  async function processUploadedFile(file: PickerFileMetadata, client: Client): Promise<void> {
    const { key, container, handle } = file;
    const url = `https://s3.amazonaws.com/${container}/${encodeURIComponent(key!)}`;
    const metadata = await client.metadata(handle, {
      width: true,
      height: true,
      mimetype: true,
      size: true,
      filename: true,
    });

    let cls = CreativeClass.FacebookImage;
    let { width, height } = metadata;
    let duration = 0;
    const { mimetype, filename } = metadata;
    const isVideo = mimetype.includes('video');
    const isGif = mimetype.includes('gif');

    if (isVideo) {
      const videoDimensions = await getVideoDimensions(url);
      width = videoDimensions.width;
      height = videoDimensions.height;
      duration = videoDimensions.duration;
      cls = CreativeClass.FacebookVideo;
    }

    if (isGif) {
      cls = CreativeClass.FacebookVideo;
    }

    const creative = Creatives.create({
      width,
      height,
      duration,
      mimetype,
      url,
      _cls: cls,
      parent: campaign.id,
      name: `${filename} (${width}x${height})`,
      is_enabled: true,
      account: account.id,
    }) as FacebookDisplayCreative;

    const validDimensions = creative.getValidDimensions(campaign);
    const validation = validateMetaCreativeDimensions(
      width,
      height,
      creative.aspectRatioDecimal,
      validDimensions,
    );

    if (!validation.isValid) {
      showValidationError(creative, filename, validation, validDimensions);
      return;
    }

    if (validation.matchingSpec) {
      creative.set({ spec: validation.matchingSpec });
    }

    if (creative instanceof FacebookVideoCreative) {
      await uploadToFacebook(creative, integration.id, account?.getFacebookAccessToken);
    }

    creatives.push(creative);
  }

  function showValidationError(
    creative: FacebookDisplayCreative,
    filename: string,
    validation: IValidationResult,
    validDimensions: Record<string, IDimensionDefinition>,
  ): void {
    const formattedCreativeRatio = creative.aspectRatio;

    let errorMessage = `"${filename}" has an unsupported aspect ratio (${formattedCreativeRatio}).`;

    if (validation.isAspectRatioValid && !validation.meetsMinDimensions) {
      errorMessage = `"${filename}" is too small. For ${formattedCreativeRatio} aspect ratio, minimum dimensions are ${validation.minWidth}x${validation.minHeight}.`;
    } else {
      const closestAspectRatio = creative.getClosestValidAspectRatio(validDimensions);
      errorMessage += ` The closest valid aspect ratio would be ${closestAspectRatio}`;
    }

    toast(errorMessage, { type: 'error' });
  }

  function handleUploadDone(client: Client): (files: PickerResponse) => void {
    return (files: PickerResponse) => {
      files.filesUploaded.forEach((file) => processUploadedFile(file, client));
    };
  }

  async function handleFileNameChange(file: PickerFileMetadata): Promise<PickerFileMetadata> {
    if (!file.filename && file.originalPath) {
      const filename = file.originalPath.split('/').pop() ?? '';
      return { ...file, filename: sanitizeFilename(filename) };
    }

    const isVideo = META_VIDEO_CONTENT_TYPES.some((type) => file.mimetype?.includes(type));

    if (isVideo && file.size > META_VIDEO_SIZE_LIMIT) {
      throw new Error('File too large. Video size limit is 1GB.');
    } else if (!isVideo && file.size > META_IMAGE_SIZE_LIMIT) {
      throw new Error('File too large. Image size limit is 20MB.');
    }

    return {
      ...file,
      filename: sanitizeFilename(file.filename),
    };
  }

  async function handleClick(): Promise<void> {
    const filestackJs = await import(/* webpackChunkName: "filestack-js" */ 'filestack-js');
    const client = filestackJs.init(FILESTACK_API_KEY);

    client
      .picker({
        onUploadDone: handleUploadDone(client),
        onFileSelected: handleFileNameChange,
        cleanupImageExif: true,
        maxFiles: 10,
        maxSize: META_VIDEO_SIZE_LIMIT,
        accept: ['image/jpeg', 'image/png', 'image/gif', 'video/mp4'],
        transformations: {
          crop: false,
          circle: false,
          rotate: false,
        },
        dropPane: {},
      })
      .open();
  }

  return isMenuItem ? (
    <MenuItem id={'addMedia'} onClick={handleClick}>
      {t('Add image/video creative')}
    </MenuItem>
  ) : (
    <Button
      disabled={!campaign.get('facebook_page_id')}
      id={'addMedia'}
      key={'add'}
      name={'add_creative_button'}
      onClick={handleClick}
      prefix={getIconForAction('add')}
    >
      {t('Add creative')}
    </Button>
  );
}

export default observer(AddCreativeButton);
